/**
 * Contextual survival hints — CLIENT-ONLY UX, ZERO wire / ZERO sim / ZERO server.
 *
 * Born from a hard datum: real captains DIE without ever discovering repair (they never touch the 🪣 bucket
 * while the hull floods), and they FREEZE — convinced the game hung — when they try to sail while still moored
 * (⚓ down, sail up, nothing moves). Neither is a novice-only problem, so these fire for ANY captain (the
 * quest-FTUE is gone; onboarding is the Helm Coach in coach.ts + the Captain's Primer manual in primer.ts).
 *
 * Two nudges, both impossible-to-ignore-but-non-intrusive:
 *   1) REPAIR: while taking on water and NOT bailing, after a SHORT beat strongly pulse the REAL bail control and
 *      float "Taking water! HOLD 🪣 to bail". Vanishes the instant she starts bailing, the flood clears, or she
 *      sinks. Fires EARLY (first hits, not only heavy damage) so it beats a novice's time-to-sink under fire;
 *      once she's proven she can bail this session, only a DIRE flood re-alarms. Anti-spam cooldown between shows.
 *   2) ANCHOR: while moored AND applying clear movement intent (sail raised / rudder hard over) AND dead in the
 *      water, pulse the REAL ⚓ control and float "Weigh anchor to set sail! Tap ⚓". Vanishes the instant the
 *      anchor comes up. Anti-spam cooldown.
 *
 * Teaching-layer muting is per-hint (`teachingMutes`): while a teaching layer owns the screen (the Captain's
 * Primer sheet is open, or the Helm Coach is guiding), the ANCHOR and ⚖ CARGO hints hold — the coach teaches
 * those controls itself. The REPAIR/bail alarm is NEVER muted — it is life-or-death and fires regardless of
 * any teaching layer (owner 2026-07-15: no novice sinks unaware; mid-coach it simply takes over the screen).
 *
 * The gate logic (`RepairHintGate` / `AnchorHintGate`) is DOM-free and clock-injected, so it is proven in
 * isolation by `uxhints.test.ts`. `UxHints` is the thin DOM controller that renders the gates' verdict: it
 * pulses whichever button the layout currently holds (HUD Forge may have relocated it — we glue the card above
 * the live element by its rect, never a hard-coded coordinate) and never intercepts a touch (pointer-events:none),
 * so the pulsing control stays fully tappable. All knobs live in `UX_HINTS` (config.ts).
 */
import { UX_HINTS } from '@piratas/protocol';

export type RepairKnobs = typeof UX_HINTS.REPAIR;
export type AnchorKnobs = typeof UX_HINTS.ANCHOR;
export type WarehouseKnobs = typeof UX_HINTS.WAREHOUSE;
export type FirstCargoKnobs = typeof UX_HINTS.FIRST_CARGO;

/** The verdict the 🏭 warehouse-fill chip should render this frame — PURE (no DOM), so `uxhints.test.ts` proves
 *  the threshold/label math directly. `hud.ts` turns it into the chip's display/width/tint. The chip only ever
 *  shows while the hold is EMPTY (`cargoUnits <= 0`): once you're laden the ⚖ weight chip is what matters and a
 *  second "warehouse" cue would just be noise. Below `SHOW_FRAC` it stays hidden so the topbar never clutters. */
export interface WarehouseChipView {
  /** render the chip at all this frame. */
  show: boolean;
  /** the warehouse is full — flip to the strong "HAUL READY" (a full cargo run is loadable). */
  full: boolean;
  /** fill fraction 0..1 (clamped) — drives the compact "NN%" label + the progress bar width. */
  fill: number;
  /** integer percent for the label (0..100). */
  pct: number;
}

export function warehouseChipView(
  warehouseUnits: number, warehouseCap: number, cargoUnits: number, k: WarehouseKnobs,
): WarehouseChipView {
  const fill = warehouseCap > 0 ? Math.max(0, Math.min(1, warehouseUnits / warehouseCap)) : 0;
  const full = warehouseCap > 0 && fill >= k.FULL_FRAC;
  // hidden while laden (the ⚖ chip owns that state) or below the noise floor.
  const show = cargoUnits <= 0 && warehouseCap > 0 && fill >= k.SHOW_FRAC;
  return { show, full, fill, pct: Math.round(fill * 100) };
}

/** Per-frame world read for the repair gate (all already known client-side). */
export interface RepairCtx {
  alive: boolean;
  /** 0 (dry) … 1 (sunk) — the client's own `1 − integrity`. */
  waterLevel: number;
  /** the captain is actively bailing/repairing this frame (bail button/SPC held). */
  repairing: boolean;
  /** a caller-computed mute (e.g. another layer owns the screen). NOTE: no teaching layer mutes the bail alarm —
   *  see `teachingMutes` — so this is normally false for the repair gate even with the primer sheet open. */
  suppressed: boolean;
}

/** Per-frame world read for the anchor gate. */
export interface AnchorCtx {
  alive: boolean;
  /** the anchor toggle is DOWN (moored). */
  anchored: boolean;
  /** sail throttle 0..1 the captain has set (intent to get under way). */
  sailTarget: number;
  /** steering input −1..1 (intent to steer). */
  rudder: number;
  /** live ship speed (sim m/s) — near zero confirms the mooring is what holds her. */
  speed: number;
  suppressed: boolean;
}

export interface GateVerdict {
  /** the hint should be visible this frame. */
  active: boolean;
  /** this frame is the RISING edge (newly raised) — the moment to ring SFX / bump the lifetime counter. */
  justShown: boolean;
}

/**
 * PURE emergency-repair gate. Holds only counters/timestamps; no DOM, no wall clock (the caller passes `now`).
 *
 * Rationing philosophy (recalibrated for novices — owner 2026-07-15): teach EVERY captain who hasn't yet proven
 * she knows the bucket. Until she has bailed at least once this session (`bailedThisSession`), any real flood
 * earns the early nudge; once she has, only a DIRE flood (≥ REPEAT_WATER) re-alarms — so a veteran who takes
 * chip damage every fight is never nagged. `shownCount` is kept purely as a persisted lifetime tally for the
 * debug/telemetry hook; it no longer gates (a lifetime cap could silence a still-struggling newcomer across
 * sessions — exactly the hole that let novices sink unaware). Trivially unit-testable with a fake clock.
 */
export class RepairHintGate {
  private dangerSince = -1;   // first `now` of the current uninterrupted flooding-without-bailing window
  private active = false;
  private lastHideAt = -Infinity;
  private bailedThisSession = false;  // she has held the 🪣 bucket at least once this session (⇒ she knows it)
  shownCount: number;

  constructor(private readonly k: RepairKnobs, shownCount = 0) { this.shownCount = shownCount; }

  step(now: number, c: RepairCtx): GateVerdict {
    // The instant she bails, mark that she KNOWS the bucket this session (future mild floods won't re-teach).
    if (c.alive && c.repairing) this.bailedThisSession = true;
    // "Taking on water" = alive, genuinely flooding, NOT already bailing, and not muted (teaching layers mute
    // anchor/cargo, never repair — see `teachingMutes` — but a caller may still suppress for its own reasons).
    const taking = c.alive && !c.suppressed && c.waterLevel >= this.k.ONSET_WATER && !c.repairing;
    if (!taking) { // dry / bailing / dead / suppressed → reset the window and drop the hint immediately
      this.dangerSince = -1;
      if (this.active) { this.active = false; this.lastHideAt = now; }
      return { active: false, justShown: false };
    }
    if (this.dangerSince < 0) this.dangerSince = now;
    if (this.active) return { active: true, justShown: false }; // already up — hold it until she bails / clears
    // Eligible if she hasn't yet proven she can bail this session, OR the flood is dire (re-alarm regardless).
    const eligible = !this.bailedThisSession || c.waterLevel >= this.k.REPEAT_WATER;
    const cooled = now - this.lastHideAt >= this.k.COOLDOWN_MS;
    if (eligible && cooled && now - this.dangerSince >= this.k.DELAY_MS) {
      this.active = true; this.shownCount += 1;
      return { active: true, justShown: true };
    }
    return { active: false, justShown: false };
  }

  isActive(): boolean { return this.active; }
}

/**
 * Should the Captain's Primer sheet (the only remaining teaching overlay) mute this hint while it is OPEN?
 * The 🪣 bail alarm is life-or-death, so it fires regardless (owner mandate: no novice sinks unaware) —
 * governed by `UX_HINTS.REPAIR.TEACHING_SUPPRESSED` (false). The ⚓ anchor and ⚖ cargo hints hold while the
 * sheet covers the screen (their pulsing targets sit under it) and fire normally the instant it closes. PURE.
 */
export function teachingMutes(kind: HintKind, teachingOpen: boolean): boolean {
  if (!teachingOpen) return false;
  return kind === 'repair' ? UX_HINTS.REPAIR.TEACHING_SUPPRESSED : true;
}

/**
 * PURE weigh-anchor gate. Same shape: fires after a short beat of clear movement intent while moored + dead in
 * the water; drops the instant the anchor is weighed (or she starts making way); anti-spam cooldown between shows.
 */
export class AnchorHintGate {
  private intentSince = -1;
  private active = false;
  private lastHideAt = -Infinity;

  constructor(private readonly k: AnchorKnobs) {}

  step(now: number, c: AnchorCtx): GateVerdict {
    const trying = c.alive && !c.suppressed && c.anchored && c.speed <= this.k.MAX_SPEED &&
      (c.sailTarget >= this.k.MIN_SAIL || Math.abs(c.rudder) >= this.k.MIN_RUDDER);
    if (!trying) {
      this.intentSince = -1;
      if (this.active) { this.active = false; this.lastHideAt = now; }
      return { active: false, justShown: false };
    }
    if (this.intentSince < 0) this.intentSince = now;
    if (this.active) return { active: true, justShown: false };
    const cooled = now - this.lastHideAt >= this.k.COOLDOWN_MS;
    if (cooled && now - this.intentSince >= this.k.DELAY_MS) {
      this.active = true;
      return { active: true, justShown: true };
    }
    return { active: false, justShown: false };
  }

  isActive(): boolean { return this.active; }
}

/** Per-frame world read for the first-cargo delivery hint. */
export interface FirstCargoCtx {
  /** trade cargo aboard (units) — a rising 0→laden edge means a run just came aboard (auto-loaded at the base). */
  cargoUnits: number;
  /** the captain has ALREADY delivered a cargo run at least once (persisted per device) — retire the hint for good. */
  delivered: boolean;
  /** the primer sheet is open — suppress so the two teaching layers never stack. */
  suppressed: boolean;
}

/**
 * PURE first-cargo-delivery gate. Fires on the rising edge of a laden hold (a run just loaded, typically the
 * base auto-load) so a fresh hauler learns WHERE to cash it in — then auto-quiets after `DISPLAY_MS` (a one-shot
 * teaching toast, not a whole-voyage nag). Retires permanently the instant a delivery is known (`delivered`),
 * which the controller persists to storage; until then it re-teaches on each fresh load. Held while the primer
 * sheet is open. No DOM, no wall clock (the caller passes `now`) — proven in isolation by `uxhints.test.ts`.
 */
export class FirstCargoHintGate {
  private wasLaden = false;   // cargoUnits was >= MIN_UNITS on the previous step (the edge detector)
  private active = false;
  private shownAt = -Infinity;

  constructor(private readonly k: FirstCargoKnobs) {}

  step(now: number, c: FirstCargoCtx): GateVerdict {
    const laden = c.cargoUnits >= this.k.MIN_UNITS;
    const rising = laden && !this.wasLaden;   // 0 → laden: a run just came aboard
    this.wasLaden = laden;
    if (c.delivered || c.suppressed) { // already learned, or muted by the FTUE → never up
      if (this.active) this.active = false;
      return { active: false, justShown: false };
    }
    if (this.active) { // hold until the hold empties (delivered/lost) or the display window elapses
      if (!laden || now - this.shownAt >= this.k.DISPLAY_MS) this.active = false;
      return { active: this.active, justShown: false };
    }
    if (rising) { this.active = true; this.shownAt = now; return { active: true, justShown: true }; }
    return { active: false, justShown: false };
  }

  isActive(): boolean { return this.active; }
}

/** The live frame read the controller needs (composed by main.ts from state it already has). */
export interface UxHintFrame {
  alive: boolean;
  waterLevel: number;
  repairing: boolean;
  anchored: boolean;
  sailTarget: number;
  rudder: number;
  speed: number;
  /** trade cargo aboard (units) — drives the first-cargo delivery hint. */
  cargoUnits: number;
  /** true while the Captain's Primer sheet is open — holds anchor/cargo (never the bail alarm). */
  teachingOpen: boolean;
}

const LS_REPAIR_SHOWN = 'sor:repairHintN';
const LS_CARGO_DELIVERED = 'sor:cargoDelivered'; // '1' once the captain has delivered a run → first-cargo hint retires

export interface UxHintDeps {
  host: HTMLElement;
  /** SFX on the rising edge of a hint (not per-frame). */
  sfx?: () => void;
  /** haptic tick on the rising edge. */
  haptic?: () => void;
  /** injectable clock for tests (defaults to performance.now). */
  clock?: () => number;
}

export type HintKind = 'repair' | 'anchor' | 'cargo';

/**
 * DOM controller: composes the gates, renders their verdict as a pulsing control + a floating parchment line.
 * Priority REPAIR > CARGO > ANCHOR: a flooding hull is life-or-death; the first-cargo teaching beat outranks the
 * mere-friction mooring nudge (and they'd otherwise collide right after a base auto-load). A higher-priority
 * live hint force-suppresses the lower gates so they never fight for the screen.
 */
export class UxHints {
  private readonly repair: RepairHintGate;
  private readonly anchorGate: AnchorHintGate;
  private readonly firstCargo: FirstCargoHintGate;
  private cargoDelivered: boolean;
  private readonly clock: () => number;
  private card: HTMLElement | null = null;
  private target: HTMLElement | null = null; // the control currently pulsed
  private kind: HintKind | null = null;

  constructor(private readonly deps: UxHintDeps) {
    this.clock = deps.clock ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    let n = 0; try { n = Number(localStorage.getItem(LS_REPAIR_SHOWN)) || 0; } catch { /* private mode */ }
    let delivered = false; try { delivered = localStorage.getItem(LS_CARGO_DELIVERED) === '1'; } catch { /* private mode */ }
    this.repair = new RepairHintGate(UX_HINTS.REPAIR, n);
    this.anchorGate = new AnchorHintGate(UX_HINTS.ANCHOR);
    this.firstCargo = new FirstCargoHintGate(UX_HINTS.FIRST_CARGO);
    this.cargoDelivered = delivered;
  }

  /** The captain delivered a cargo run — retire the first-cargo teaching hint for good (persisted per device).
   *  Called by main.ts on the observed delivery edge (hold empties while alive & not respawning). */
  markCargoDelivered(): void {
    if (this.cargoDelivered) return;
    this.cargoDelivered = true;
    try { localStorage.setItem(LS_CARGO_DELIVERED, '1'); } catch { /* private mode */ }
  }

  /** Read-only introspection for the headless smoke hook / tests. */
  debug(): { kind: HintKind | null; repairShown: number; cargoDelivered: boolean } {
    return { kind: this.kind, repairShown: this.repair.shownCount, cargoDelivered: this.cargoDelivered };
  }

  /** Visual-proof / E2E ONLY: force a hint on screen, bypassing the gate timers. Pure theatre — it never
   *  changes gameplay, gate state, or the persisted lifetime counter. Pair with `clearHint()` to tear it down. */
  previewHint(kind: HintKind): void { this.showCard(kind); this.reposition(); }
  clearHint(): void { this.hide(); }

  /** Advance one HUD frame. Idempotent per state — safe to call every frame. */
  tick(f: UxHintFrame): void {
    const now = this.clock();
    // The 🪣 bail alarm is the ONE hint no teaching layer mutes (life-or-death) — `teachingMutes` encodes that,
    // so a novice taking fire with the primer sheet still up gets the nudge. Anchor/cargo hold while it's open.
    const r = this.repair.step(now, {
      alive: f.alive, waterLevel: f.waterLevel, repairing: f.repairing, suppressed: teachingMutes('repair', f.teachingOpen),
    });
    // First-cargo teaching beat — held while the primer sheet is open or the (higher-priority) repair hint is live.
    const c = this.firstCargo.step(now, {
      cargoUnits: f.cargoUnits, delivered: this.cargoDelivered, suppressed: teachingMutes('cargo', f.teachingOpen) || r.active,
    });
    // Anchor holds while the primer sheet is open or EITHER higher-priority hint is live.
    const a = this.anchorGate.step(now, {
      alive: f.alive, anchored: f.anchored, sailTarget: f.sailTarget, rudder: f.rudder, speed: f.speed,
      suppressed: teachingMutes('anchor', f.teachingOpen) || r.active || c.active,
    });
    if (r.justShown) this.persistRepairCount();
    const want: HintKind | null = r.active ? 'repair' : c.active ? 'cargo' : a.active ? 'anchor' : null;
    this.render(want);
  }

  private persistRepairCount(): void {
    try { localStorage.setItem(LS_REPAIR_SHOWN, String(this.repair.shownCount)); } catch { /* private mode */ }
  }

  private render(want: HintKind | null): void {
    if (!want) { if (this.kind) this.hide(); return; }
    if (this.kind !== want) this.showCard(want);
    this.reposition();
  }

  private showCard(kind: HintKind): void {
    this.kind = kind;
    this.ensureCard();
    const targetId = kind === 'repair' ? 'bail' : kind === 'anchor' ? 'anchor' : 'cargorunchip';
    this.setTarget(document.getElementById(targetId));
    const body = this.card!.querySelector('.uxh-body')!;
    body.innerHTML = kind === 'repair'
      ? `<span class="uxh-ic">🪣</span><span class="uxh-tx"><b>Taking on water!</b><span>HOLD 🪣 to bail her out — fast!</span></span>`
      : kind === 'anchor'
      ? `<span class="uxh-ic">⚓</span><span class="uxh-tx"><b>She's moored fast!</b><span>Weigh anchor to set sail — tap ⚓.</span></span>`
      : `<span class="uxh-ic">⚖</span><span class="uxh-tx"><b>Cargo loaded!</b><span>Deliver at any PORT's trade ring — farther from home = fatter pay. ⚓</span></span>`;
    this.card!.className = `on ${kind}`;
    this.deps.sfx?.();
    this.deps.haptic?.();
  }

  private setTarget(el: HTMLElement | null): void {
    if (this.target && this.target !== el) this.target.classList.remove('uxh-target');
    this.target = el;
    if (el) el.classList.add('uxh-target');
  }

  private hide(): void {
    this.kind = null;
    this.setTarget(null);
    if (this.card) this.card.className = '';
  }

  /** Glue the card just above the live target control (wherever HUD Forge put it), clamped on-screen. */
  private reposition(): void {
    if (!this.card || !this.target) return;
    const r = this.target.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return; // target not laid out yet
    const vw = window.innerWidth || 390;
    const vh = window.innerHeight || 844;
    const cw = this.card.offsetWidth || 220;
    let left = r.left + r.width / 2 - cw / 2;
    left = Math.max(8, Math.min(left, vw - cw - 8));
    this.card.style.left = `${Math.round(left)}px`;
    // sit ABOVE the control (never over it); if it'd clip the top, drop below instead.
    const above = vh - r.top + 12;
    if (r.top - this.card.offsetHeight - 12 < 8) {
      this.card.style.bottom = 'auto';
      this.card.style.top = `${Math.round(r.bottom + 12)}px`;
      this.card.classList.add('below');
    } else {
      this.card.style.top = 'auto';
      this.card.style.bottom = `${Math.round(above)}px`;
      this.card.classList.remove('below');
    }
  }

  private ensureCard(): void {
    if (this.card) return;
    if (!document.getElementById('uxh-style')) {
      const st = document.createElement('style'); st.id = 'uxh-style';
      st.textContent = `
        #uxhint{ position:fixed; z-index:46; left:0; bottom:200px; width:min(74vw,232px); pointer-events:none;
          opacity:0; transform:translateY(6px) scale(.96); transition:opacity .3s ease, transform .3s ease; font-family:inherit; }
        #uxhint.on{ opacity:1; transform:translateY(0) scale(1); }
        #uxhint .uxh-body{ display:flex; align-items:flex-start; gap:10px; padding:11px 14px; border-radius:14px;
          background:linear-gradient(165deg, rgba(20,34,52,.96), rgba(8,16,28,.98)); color:#eef6ff;
          box-shadow:0 14px 40px rgba(0,8,16,.62), inset 0 1px 0 rgba(255,255,255,.08); }
        #uxhint .uxh-ic{ font-size:24px; line-height:1.1; filter:drop-shadow(0 1px 3px #000); }
        #uxhint .uxh-tx{ display:flex; flex-direction:column; gap:2px; min-width:0; }
        #uxhint .uxh-tx b{ font-size:14.5px; font-weight:900; text-shadow:0 1px 3px #000; }
        #uxhint .uxh-tx span{ font-size:12px; font-weight:600; line-height:1.36; color:rgba(214,228,244,.86); }
        /* a small caret aimed at the pulsing control */
        #uxhint .uxh-body::after{ content:''; position:absolute; left:50%; bottom:-7px; transform:translateX(-50%) rotate(45deg);
          width:13px; height:13px; border-radius:3px; }
        #uxhint.below .uxh-body::after{ bottom:auto; top:-7px; }
        #uxhint .uxh-body{ position:relative; }
        /* REPAIR theme — cyan water / alarm */
        #uxhint.repair .uxh-body{ border:1px solid rgba(79,200,255,.7); box-shadow:0 14px 40px rgba(0,8,16,.62), 0 0 26px -6px rgba(79,200,255,.55), inset 0 1px 0 rgba(255,255,255,.08); }
        #uxhint.repair .uxh-tx b{ color:#bfeeff; }
        #uxhint.repair .uxh-body::after{ background:linear-gradient(135deg, rgba(20,34,52,.96), rgba(8,16,28,.98)); box-shadow:1px 1px 0 rgba(79,200,255,.7); }
        #uxhint.repair.below .uxh-body::after{ box-shadow:-1px -1px 0 rgba(79,200,255,.7); }
        /* ANCHOR theme — warm amber / moored */
        #uxhint.anchor .uxh-body{ border:1px solid rgba(255,206,110,.75); box-shadow:0 14px 40px rgba(0,8,16,.62), 0 0 26px -6px rgba(255,190,90,.5), inset 0 1px 0 rgba(255,255,255,.08); }
        #uxhint.anchor .uxh-tx b{ color:#ffe6a8; }
        #uxhint.anchor .uxh-body::after{ background:linear-gradient(135deg, rgba(20,34,52,.96), rgba(8,16,28,.98)); box-shadow:1px 1px 0 rgba(255,206,110,.75); }
        #uxhint.anchor.below .uxh-body::after{ box-shadow:-1px -1px 0 rgba(255,206,110,.75); }
        /* CARGO theme — trade-goods teal (cases the ⚖ weight chip). It anchors to the TOPBAR chip, so it drops
           into the top-left where the chat/menu float buttons (z60) live — lift just this card above them so the
           teaching copy is never occluded (pointer-events:none keeps the button beneath fully tappable). */
        #uxhint.cargo{ z-index:63; }
        #uxhint.cargo .uxh-body{ border:1px solid rgba(127,233,198,.75); box-shadow:0 14px 40px rgba(0,8,16,.62), 0 0 26px -6px rgba(127,233,198,.5), inset 0 1px 0 rgba(255,255,255,.08); }
        #uxhint.cargo .uxh-tx b{ color:#b8f5df; }
        #uxhint.cargo .uxh-body::after{ background:linear-gradient(135deg, rgba(20,34,52,.96), rgba(8,16,28,.98)); box-shadow:1px 1px 0 rgba(127,233,198,.75); }
        #uxhint.cargo.below .uxh-body::after{ box-shadow:-1px -1px 0 rgba(127,233,198,.75); }
        /* the STRONG pulse on the real control — id-level selectors so it beats #bail.needs' subtler pulse.
           pointer-events untouched, so the flashing button stays fully tappable/holdable. */
        #hud #bail.uxh-target, #hud #anchor.uxh-target{ z-index:6; }
        #hud #bail.uxh-target{ animation:uxhPulseCyan .95s ease-out infinite; }
        #hud #anchor.uxh-target{ animation:uxhPulseAmber .95s ease-out infinite; }
        /* the ⚖ chip lives in the topbar — a tighter teal ring so it reads without swamping neighbouring chips */
        #topbar #cargorunchip.uxh-target{ position:relative; z-index:6; animation:uxhPulseTeal .95s ease-out infinite; }
        @keyframes uxhPulseCyan{ 0%{ box-shadow:0 0 0 0 rgba(79,200,255,.85), 0 0 0 3px rgba(79,200,255,.95); }
          70%{ box-shadow:0 0 0 18px rgba(79,200,255,0), 0 0 0 3px rgba(79,200,255,.5); }
          100%{ box-shadow:0 0 0 0 rgba(79,200,255,0), 0 0 0 3px rgba(79,200,255,0); } }
        @keyframes uxhPulseAmber{ 0%{ box-shadow:0 0 0 0 rgba(255,196,90,.85), 0 0 0 3px rgba(255,206,110,.95); }
          70%{ box-shadow:0 0 0 18px rgba(255,196,90,0), 0 0 0 3px rgba(255,206,110,.5); }
          100%{ box-shadow:0 0 0 0 rgba(255,196,90,0), 0 0 0 3px rgba(255,206,110,0); } }
        @keyframes uxhPulseTeal{ 0%{ box-shadow:0 0 0 0 rgba(127,233,198,.85), 0 0 0 2px rgba(127,233,198,.95); }
          70%{ box-shadow:0 0 0 12px rgba(127,233,198,0), 0 0 0 2px rgba(127,233,198,.5); }
          100%{ box-shadow:0 0 0 0 rgba(127,233,198,0), 0 0 0 2px rgba(127,233,198,0); } }`;
      document.head.appendChild(st);
    }
    const el = document.createElement('div'); el.id = 'uxhint';
    el.innerHTML = '<div class="uxh-body"></div>';
    this.deps.host.appendChild(el);
    this.card = el;
  }
}
